import { API, deleteRequest, getRequest, patchRequest, postRequest, putRequest } from '@/api/api';
import type { FileDto } from '@/api/api.dto';

import {
  mapAddContactsToDto,
  mapAssignSeatToDto,
  mapContact,
  mapPeoplePage,
  mapPerson,
  mapPreferencesToDto,
  mapRemoveSeatToDto,
  mapUpdateContactInputToDto,
  mapUpdatePersonInputToDto,
} from './people.mappers';
import type {
  ActivationStatus,
  AddContactsRequestDto,
  AssignSeatDto,
  AssignSeatInput,
  ChangePreferencesRequestDto,
  Contact,
  ContactDto,
  GetAllPeopleParams,
  GetPeopleParams,
  NewContact,
  PageDto,
  Person,
  PersonDto,
  PersonPreferences,
  RemoveSeatDto,
  RemoveSeatInput,
  SetActivationStatusRequestDto,
  UpdateContactInput,
  UpdateContactRequestDto,
  UpdatePersonInput,
  UpdatePersonRequestDto,
} from './people.types';

/**
 * `GET /?service_id=…` → the people on one service, as full `PersonResponse` objects:
 * avatar, name, and a `services[]` entry per membership carrying `role` and `seat_type`.
 * That makes this the only call needed to know who occupies which subscription seat —
 * `GET /crew` reports neither.
 *
 * The service is named twice: as the required query param the route filters on, and as the
 * header the service-scoping middleware demands.
 */
export const getPeople = async (params: GetPeopleParams): Promise<Person[]> => {
  const { data } = await getRequest<PageDto<PersonDto>>(
    API.PEOPLE,
    '',
    false,
    {
      limit: params.limit ?? 1000,
      page: params.page ?? 1,
      service_id: params.serviceId,
      ...(params.search ? { search: params.search } : {}),
    },
    params.serviceId,
  );
  return mapPeoplePage(data).items;
};

export const getAllPeople = async (params: GetAllPeopleParams = {}): Promise<Person[]> => {
  const { data } = await getRequest<PageDto<PersonDto>>(API.PEOPLE, '/all', false, {
    limit: params.limit ?? 1000,
    page: params.page ?? 1,
    ...(params.search ? { search: params.search } : {}),
  });
  return mapPeoplePage(data).items;
};

export const getPersonById = async (id: string): Promise<Person> => {
  const { data } = await getRequest<PersonDto>(API.PEOPLE, `/${id}`);
  return mapPerson(data);
};

export const getMe = async (): Promise<Person> => {
  const { data } = await getRequest<PersonDto>(API.PEOPLE, '/me');
  return mapPerson(data);
};

export const getPeopleByIds = async (ids: string[]): Promise<Person[]> => {
  if (!ids.length) return [];
  const { data } = await getRequest<PersonDto[]>(API.PEOPLE, '/ids', false, {
    ids: [...new Set(ids)],
  });
  return (data ?? []).map(mapPerson);
};

export const updatePerson = async (id: string, input: UpdatePersonInput): Promise<Person> => {
  const { data } = await putRequest<PersonDto, UpdatePersonRequestDto>(
    API.PEOPLE,
    `/${id}`,
    mapUpdatePersonInputToDto(input),
  );
  return mapPerson(data);
};

export const setActivationStatus = async (
  id: string,
  activationStatus: ActivationStatus,
): Promise<void> => {
  await patchRequest<void, SetActivationStatusRequestDto>(API.PEOPLE, `/${id}/activation`, {
    activation_status: activationStatus,
  });
};

export const onboardPerson = async (id: string): Promise<void> => {
  await patchRequest<void, undefined>(API.PEOPLE, `/${id}/onboard`, undefined);
};

export const updateAvatar = async (id: string, file: File): Promise<FileDto> => {
  const formData = new FormData();
  formData.append('avatar', file);
  const { data } = await patchRequest<FileDto, FormData>(API.PEOPLE, `/${id}/avatar`, formData);
  return data;
};

export const addContacts = async (id: string, contacts: NewContact[]): Promise<Contact[]> => {
  const { data } = await postRequest<ContactDto[], AddContactsRequestDto>(
    API.PEOPLE,
    `/${id}/contacts`,
    mapAddContactsToDto(contacts),
  );
  return (data ?? []).map(mapContact);
};

export const updateContact = async (id: string, input: UpdateContactInput): Promise<void> => {
  await putRequest<void, UpdateContactRequestDto>(
    API.PEOPLE,
    `/${id}/contacts`,
    mapUpdateContactInputToDto(input),
  );
};

export const deleteContacts = async (id: string, contactIds: string[]): Promise<void> => {
  await deleteRequest<void>(API.PEOPLE, `/${id}/contacts`, undefined, false, {
    ids: contactIds,
  });
};

/**
 * `PATCH /roles/seat` → 204. Sets which subscription seat a person occupies.
 *
 * The server checks three things and nothing else: the caller holds `SeatManagement` on the
 * service (403, from the middleware in `server/server.go` — the owner and the captain both
 * do by default, and an owner may withdraw it), the seat type matches the service's current
 * tier (400), and the seat is not already full (403). It does **not** restrict which role may
 * hold which seat — that rule lives in `subscription.seats`, on this side only.
 *
 * A person already holding the other seat type is *moved*, not duplicated: `seat_type` is
 * a single column on their role row.
 *
 * The service is named twice, as on `getPeople`: in the body the handler reads, and in the
 * header the seat-management middleware reads. Both routes are permission-gated, and that
 * gate resolves the service from the query or `X-Service-Id` only — a body it never parses.
 * Without the header it answers 401 before the handler runs.
 */
export const assignSeat = async (input: AssignSeatInput): Promise<void> => {
  await patchRequest<void, AssignSeatDto>(
    API.PEOPLE,
    '/roles/seat',
    mapAssignSeatToDto(input),
    false,
    input.serviceId,
  );
};

/**
 * `DELETE /roles/seat` → 204. Empties the seat a person holds, leaving their role untouched:
 * the seat is freed by resetting `seat_type` to `harbor_seat`, not by removing them from the
 * vessel. Carries `X-Service-Id` for the same reason `assignSeat` does.
 *
 * Two consequences of it being a reset rather than a clear. The owner's seat is refused
 * outright (403) — theirs comes with the vessel. And on the harbor tier, where `harbor_seat`
 * *is* the pool's seat, the call succeeds while changing nothing; `isPaidSeat` in
 * `subscription.seats` keeps that case from ever being offered.
 */
export const removeSeat = async (input: RemoveSeatInput): Promise<void> => {
  await deleteRequest<void, RemoveSeatDto>(
    API.PEOPLE,
    '/roles/seat',
    mapRemoveSeatToDto(input),
    false,
    undefined,
    input.serviceId,
  );
};

export const changePreferences = async (
  id: string,
  preferences: PersonPreferences,
): Promise<void> => {
  await putRequest<void, ChangePreferencesRequestDto>(
    API.PEOPLE,
    `/${id}/preferences`,
    mapPreferencesToDto(preferences),
  );
};
